<template>
  <div id="treatment-parent">
    <div class="loader" v-if="loadingAnimation">
      <div class="ball-grid-pulse">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
    </div>
    <div class="animation-message" v-show="animationMessageSubmitting">SUBMITTING SYMPTOMS</div>
    <div class="animation-message" v-show="animationMessageProcessing">PROCESSING CONDITIONS</div>
    <div class="animation-message" v-show="animationMessageReturning">RETURNING CONDITIONS</div>

    <div id="button-container" v-if="!loadingAnimation">
      <div class="button" :style="[viewType == 'symptoms' ? { opacity: 1 } : { opacity: 0.4 }]" v-on:click="viewType = 'symptoms'">Select Symptoms</div>
      <div class="button" :style="[viewType == 'causes' ? { opacity: 1 } : { opacity: 0.4 }]" v-on:click="viewType = 'causes'">Common Causes</div>
    </div>

    <div id="checkbox-results-container" v-if="viewType == 'causes'">
      <div class="checkbox-results-item-container" v-for="(cause, index) in causeList" :key="index">
        <div class="checkbox-results-item-header">
          <div class="checkbox-results-item-title">
            {{ cause.name }}
          </div>
        </div>

        <vue-markdown>{{ cause.overview }}</vue-markdown>
        <div class="checkbox-results-treatment-title" v-if="cause.treatment">TREATMENT PLAN</div>
        <div class="checkbox-results-treatment-spacer" v-if="cause.treatment"></div>
        <vue-markdown v-if="cause.treatment">{{ cause.treatment }}</vue-markdown>
      </div>
    </div>

    <div id="checkbox-list-container" v-if="!loadingAnimation && !conditions.length && viewType == 'symptoms'">
      <div class="checkbox-list-container-item no-select" v-for="(symptom, index) in symptoms" :key="index">
        <label><input type="checkbox" v-model="symptoms[index].checked" /> {{ symptom.symptom_name }}</label>
      </div>
    </div>

    <div id="checkbox-button-container" v-if="!conditions.length && !loadingAnimation && viewType == 'symptoms'">
      <button class="button" @click="saveButton">SUBMIT</button>
    </div>

    <div id="error-container" v-if="errorMessage && !loadingAnimation && viewType == 'symptoms'">
      {{ errorMessage }}
    </div>

    <div id="results-text-container" v-if="numberOfConditions.length">
      <div class="results-text" v-if="!loadingAnimation">Your symptoms have been analyzed and individually matched to a list of medical conditions that are known to cause brain fog. Please note that the conditions are sorted by how accurately they match your symptoms, so we recommend you start from the top and work your way down. Feel free to enter your email address in the box below to have a copy of the results sent to you.</div>
    </div>

    <input id="email-address-box" type="email" v-if="conditions.length && !loadingAnimation && !submittedEmail" v-model="emailAddress" placeholder="Enter your email address" required />

    <button id="email-button" v-if="conditions.length && !loadingAnimation && !submittedEmail" @click="emailButton">Send Email</button>

    <div class="results-text" style="margin-bottom: 10px; font-weight: 600" v-if="submittedEmail">An email has been sent to the address provided</div>

    <div id="checkbox-results-container" v-if="conditions.length && !loadingAnimation">
      <div class="checkbox-results-item-container" v-for="condition in conditions" :key="condition.id">
        <div class="checkbox-results-item-header">
          <div class="checkbox-results-item-probability" :style="condition.colour">
            {{ condition.matchLevel }}
          </div>
          <div class="checkbox-results-item-title">
            {{ condition.name }}
          </div>
        </div>
        <vue-markdown>{{ condition.overview }}</vue-markdown>
        <div class="checkbox-results-treatment-title" v-if="condition.treatment">TREATMENT PLAN</div>
        <div class="checkbox-results-treatment-spacer" v-if="condition.treatment"></div>
        <vue-markdown v-if="condition.treatment">{{ condition.treatment }}</vue-markdown>
      </div>
    </div>

    <div v-if="conditions.length && !loadingAnimation">
      <button class="button" @click="refreshPage">Start Again</button>
    </div>

    <div v-if="conditions.length && !loadingAnimation">
      <div style="margin-bottom: 5px">This website is 100% free to use with no ads. If you'd like to make a Paypal donation to help keep it running, you can do so with the button below. Thank you.</div>
      <form style="margin-bottom: 20px" action="https://www.paypal.com/donate" method="post" target="_top">
        <input type="hidden" name="hosted_button_id" value="W587NCHZDER8A" />
        <input type="image" src="https://www.paypalobjects.com/en_US/i/btn/btn_donateCC_LG.gif" border="0" name="submit" title="PayPal - The safer, easier way to pay online!" alt="Donate with PayPal button" />
        <img alt="" border="0" src="https://www.paypal.com/en_NZ/i/scr/pixel.gif" width="1" height="1" />
      </form>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Vue from "vue";
import VueMarkdown from "vue-markdown";
Vue.use(require("vue-cookies"));
Vue.$cookies.config("7d");

Vue.use(VueMarkdown);

const { v4: uuidv4 } = require("uuid");

export default {
  name: "symptoms",
  components: {
    VueMarkdown,
  },
  data() {
    return {
      loadingAnimation: false,
      animationMessageSubmitting: false,
      animationMessageProcessing: false,
      animationMessageReturning: false,
      symptoms: [],
      userSymptoms: "",
      checkedCount: 0,
      errorMessage: "",
      numberOfConditions: [],
      conditions: [],
      submittedConditions: false,
      emailAddress: "",
      submittedEmail: false,
      viewType: "symptoms",

      causeList: [
        {
          name: "Poor Gut Health",
          overview: `Our gut plays a huge role in the overall health of our body. We have around 100 million neurons in our gut - that's more than our nervous system and spinal cord! These gut neurons generate much of our dopamine and serotonin, so it's very important that we keep our gut healthy. Diets high in refined sugar have been proven to reduce the production of proteins that form new memories. Mental illnesses such as schizophrenia are connected to gut inflammation. The evidence is clear and is only getting stronger - poor gut health affects many aspects of our overall health, such as the brain, heart, immune system, skin, weight, hormone levels and ability to absorb nutrients.`,
          treatment: `Thankfully our gut can bounce back once we treat it right. Most importantly, we need to manage what we are putting into it. We recommend following the points below to get your gut health back where it should be:<ul><li>Increase consumption of fermented food such as sauerkraut and other fermented vegetables. </li><li>Consume kefir daily. Milk kefir is a probiotic powerhouse capable of single-handedly transforming the gut. It's cheap, easy to make and delicious in smoothies! It comes premade in a powder form, or you can make it yourself with milk kefir grains and have an endless supply. All you need to make a new batch is milk. You can buy some starter milk kefir grains from Amazon <a href="https://www.amazon.com/Kefir-Grains-Living-Probiotic-Enriched/dp/B004RCSWGG/ref=sr_1_1?dchild=1&keywords=milk+kefir&qid=1595553847&sr=8-1" target="_blank">here</a> . Definitely go with the kefir grains over the powder, and try to drink at least a cup of milk kefir twice daily.</li><li>Consider supplmenting with high strength probiotic pills. <a href="https://www.amazon.com/Probiotics-Formulated-Probiotic-Supplement-Acidophilus/dp/B079H53D2B/ref=sr_1_5?dchild=1&keywords=Probiotics+60+Billion+CFU&qid=1596625897&sr=8-5" target="_blank">These</a> have excellent reviews. If you're tight on money have to choose between milk kefir and probiotics, definitely go with milk kefir, the probiotic count of a cup of kefir is many times higher than even the best probiotic pills! </li><li>Reduce your sugar and sweetener intake. Cutting out food that has a negative effect on your gut health is critical. Try to reduce the amount of processed foods you eat as well. </li><li>Avoid antibiotics unless you absolutely must take them. Antibiotics are incredibly damaging to the good bacteria in your gut and should only be used as a last resort. They may have contributed to your poor gut health in the first place!</li></ul>Your gut will take time to get back to full health. It may take around 12 weeks for injured intestinal mucosal lining to fully heal. For patients with celiac disease, their intestinal permeability became normal after around a year of following a gluten free diet.`,
        },

        {
          name: "Vitamin B12 Deficiency",
          overview: `Vitamin B12 plays an essential role in the production of red blood cells and DNA, as well as other functions like keeping your nervous system functioning properly. Vegetarians and vegans are much more likely to be deficient in B12 as it's not naturally found in plants, only animal products. Being deficient in B12 isn't always caused by your diet as certain conditions such as celiacs or Chron's disease can interfere with nutrient absorption and cause a deficiency`,
          treatment: `Vitamin deficiencies are picked up in standard blood tests - see our blood test section at the bottom of the page for our full recommendation. Treatment depends on how severe the deficiency is. For serious cases you will need weekly B12 shots, or daily high dose B12 pills.`,
        },
        {
          name: "Elevated Histamine",
          overview: `Histamine is vital to regulate many bodily functions including blood pressure, sleep, digestion and mucus secretion. It also functions as a neurotransmitter in the brain, carrying chemical messages between neurons. Under normal circumstances, the body produces small amounts of histamine to maintain balance among different body systems. But when the body responds to stressors such as allergies, wounds, or foreign pathogens our white blood cells (mast cells and basophils) secrete large quantities of histamine as part of the immune response. Prolonged accumulation of histamine in the body creates the negative symptoms associated with histamine intolerance, which can be caused by either increased histamine availability or impaired histamine degradation.`,
          treatment: `Excess histamine can be measured in blood or urine, so it's important to get tested if you suspect that you have a histamine overload in your body. There are a variety of causes of histamine overload such as:<br><br><b>Inflammation Causing Foods</b><br>Most foods contain histamine, but some foods have much higher levels. Foods containing high levels of histamine contribute to histamine overload and if regularly consumed can overwhelm the body's ability to keep up with breaking it down. There are also certain foods that promote inflammation in the body which indirectly lead to increased levels of histamine. <a href="https://www.healthline.com/health/low-histamine-diet" target="_blank">This page</a> has some great information on low histamine diets.<br><br><b>Reduced DAO Activity</b><br>DAO is an enzyme responsible for breaking down histamine in your body. If DAO levels are impaired or insufficient to deal with the amount of histamine being synthesized or ingested, histamine levels will increase. DAO enzymes can be purchased from Amazon <a href="https://amzn.to/3grdwZt" target="_blank">here</a> which when taken will help break down excess histamine in your body. A number of factors can influence DAO production and availability including:<br><ul>   <li>Diet</li> <li>Vitamin deficiencies</li><li>Genetics</li><li>Long-term NSAID use</li><li>Medications</li><li>Alcohol consumption</li></ul><b>Digestive Imbalances & Bacterial Overgrowth</b><br>A number of gastrointestinal imbalances can contribute to histamine intolerance. Healing the gut through probiotics, milk kefir and clean food is essential if you suspect that you have gut health issues.<br><br><b>High Stress Lifestyle</b><br>Chronic stress can habitually trigger our fight or flight response and significantly contributes to overall histamine load. There are also a number of other hormones impacted by chronic stress that directly and indirectly impact histamine levels including estrogen, progesterone, cortisone, and thyroid hormones. It's essential that you remove as many causes of stress in your life as possible to reduce the amount of histamine released by your body.`,
        },
        {
          name: "Food Allergies",
          overview: `Allergies cause inflammation, and inflammation can wreak havoc on the body and cause a range of symptoms including brain fog. Inflammation can be a direct result of certain environmental exposure, or the consumption of certain foods. Common environmental allergies include dust mites, pollen, pet dander, mold and cigarette smoke. Common food allergies include peanuts, soy, egg, wheat, tree nut, shellfish, fish, sesame, milk and lectins. `,
          treatment: `<b>Environmental Allergies</b> can be very difficult or impossible to completely avoid. They are typically treated with a general purpose antihistamine. You should avoid first generation antihistamine which often cause drowsiness and often exascebate brain fog. Choose something with the active ingredient of fexofenadine, cetirizine or loratadine and see if it helps with your symptoms. Synthetic antihistamines should not be taken long term, so if you find them effective you should instead look into natural antihistamines which are surprisingly effective and don't increase the risk of developing diseases like dementia which synthetic anthistamines are linked to. Some of the most effective natural antihistamines are <a href="https://amzn.to/2XrzoMV" target="_blank">stinging nettle extract</a>, <a href="https://amzn.to/31drWWV" target="_blank">quercetin with bromelain</a>  and<a href="https://amzn.to/33nfaYt" target="_blank"> reishi mushroom extract</a>.  <br><br><b>Food Allergies</b> are a very common cause of brain fog. There are two primary methods of diagnosing food allergies - blood/skin tests and elimination diets.  Blood tests look for antibodies in your blood that are specific to a certain food or other allergen. The more antibodies the higher chance that you're allergic to that food.  These tests are great for getting an idea of what you may be allergic to, but they aren't particularly accurate at only around 60%.  The preferred method of identifying allergies is through an elimination diet, which involves removing foods from your diet that you suspect you don't tolerate. You can first try to eliminate individual food groups for at least a couple of weeks at a time and see if you notice any improvement in your symptoms. Some people are sensitive to multiple foods so this approach doesn't work well for everyone. What you'll likely need to do in this case is implement a very strict elimination diet across multiple food groups at once. I'd recommend either the <a href="https://www.monashfodmap.com/ibs-central/i-have-ibs/starting-the-low-fodmap-diet" target="_blank">FODMAP</a>  diet, or the <a href="https://meat.health/knowledge-base/carnivore-diet-what-to-eat/" target="_blank">carnivore</a> diet, which involves only eating animal products. If you go with the carnivore diet I recommend eating nothing but meat - so no eggs, cheese or milk etc. This is fairly extreme but if you are desperate for relief, and nothing else has helped to improve your brain fog and other symptoms, it's worth a shot. The Inuits were very healthy and  survived on a mostly meat only diet all year round, so you'll be fine to give carnivore a go for a few weeks if you choose to. If your symptoms improve on either of these diets then you can start slowly adding food groups back in (one at a time) and see how your body reacts.`,
        },
        {
          name: "Iron Deficiency",
          overview: `Lacking in iron is the most common nutrient deficiency in the world, with women being most at risk, especially those on vegetarian/vegan diets. If your need for dietary iron isn’t met, your body’s iron stores will become depleted. This can lead to iron deficiency, also known as low iron levels, and, if you become severely depleted, anaemia.`,
          treatment: `Iron deficiencies are picked up in standard blood tests - see our blood test section at the bottom of the page for our full recommendation. Treatment is typically just an iron tablet per day. You may need to take the supplements for several months. Iron supplements may cause constipation or black stools.`,
        },
        {
          name: "Mold Exposure",
          overview: `Illnesses caused by toxic mold are under-diagnosed, and more prevalent than you might expect. They manifest in many different ways, including with symptoms that are psychiatric - such as depression, anxiety, attentional problems, brain fog and insomnia. Mold toxins can accumulate in the body, continuing to wreak havoc long after exposure ceases and require an extended course of treatment. <br>Mold allergies and mold toxicity are not the same illness. Mold allergies are due to mold spores that are inhaled and cause hay fever like symptoms. This is different than toxic mold, which is due to the volatile toxic vapors produced by mold that can cause a chronic inflammatory response, (CIRS), an auto-immune reaction caused by poor clearance of bio toxins in vulnerable individuals. <br>Mold toxicity can manifest with so many different and diverse symptoms, that it is frequently misdiagnosed. It is likely that some cases of chronic fatigue, fibromyalgia, mast cell activation disorder, histamine intolerance, irritable bowel and leaky gut, multiple sclerosis and post-treatment Lyme syndrome, which do not respond to treatment, are due to a chronic inflammatory response caused by toxic mold. Unfortunately mold toxicity is not on the radar of many practitioners, and thus they do not include it as part of the differential diagnosis.`,
          treatment: `The first and most crucial step in treatment, is to identify where the mold is located and to get away from it. There are companies that specialize in mold testing and others that offer mold remediation. It's recommended to use different companies for testing and remediation, though some companies do offer both, in order to avoid a conflict of interest. <br> A more cost effective means of testing is with mold growth plates, which can be purchased from a company called <a href="https://immunolytics.com" target="_blank">ImmunoLytics</a>. The plates are set out around the house and later collected and sent to the laboratory for analysis. There are also companies that can identify mycotoxins in the urine. <br> Once a diagnosis is made, this is a very treatable condition. It can take a long time because it must be done very carefully and slowly, or symptoms can be exacerbated by the treatment itself, as the mold toxins are mobilized.  Some treatment approaches first make use of natural binders such as charcoal and clay, which trap the mycotoxins and permit them to be excreted, as well as probiotics such as Saccromyces Boulardi. The second phase of treatment is anti-fungals.`,
        },
        {
          name: "Sleep Apnea / Upper Airway Restriction Syndrome",
          overview: `Sleep Apnea is a common and under diagnosed sleeping disorder. Don’t assume that only overweight people are affected by this condition, it affects young and old, overweight and underweight. It’s a very common cause of brain fog, so if you often wake up feeling unrefreshed and lack energy throughout the day you may be suffering from it. The most common form of sleep apnea is 'obstructive', where you stop breathing while you are asleep due to your tongue muscles relaxing and closing your airways. This is relatively easy to diagnose via a sleep study.<br><br>UARS is a particularly insidious form of sleep apnea, it stands for Upper Airway Restriction Syndrome. It often doesn’t show up on many sleep studies so may be missed even by experienced sleep technicians. Instead of your airways being fully obstructed they are just narrowed, which reduces your oxygen blood level and causes subconscious arousals. Imagine someone prodding you multiple times every hour while you try to sleep! UARS is no different, and its effects are often just as severe and damaging as the classic 'obstructive' form.  It is strongly advised that you consider our treatment recommendations below even if you’re confident that you don’t have sleep apnea.`,
          treatment: `Firstly, if you have never had a sleep study done this is the first thing to push for. Sleep studies can be performed at home or at dedicated sleep clinics. Studies at sleep clinics are usually superior as they have better equipment and more data points to analyze. You'll likely need to book in an appointment with your primary care physician who should be able to refer you on to have a sleep study. If you're having trouble getting a sleep study referral you should be able to find a company that will post you an overnight sleep study device for a fee.  <br>If you're diagnosed with moderate or severe sleep apnea then treatment with a CPAP device is standard. Continuous Positive Airway Pressure devices force air through your mouth and/or nose to ensure you're breathing correctly when sleeping.  <br>If however you're diagnosed with 'mild' sleep apnea (which often is not treated), or told that you don't have sleep apnea at all, you actually may still have a sleep disorder such as UARS. Many sleep clinics don't actively look for UARS, so I recommend looking into the options below if you've ruled out many of the other potential causes for your brain fog.<br><ul><li>Mandibular splints are custom made dental devices that are designed to keep your lower jaw and tongue forward while you sleep. They are most effective at treating 'mild' sleep apnea and UARS. Since they are custom made they are expensive, so a great cheap alternative is an adjustable mouth guard, such as <a href="https://amzn.to/39VwqW2" target="_blank">this</a>  one sold on Amazon. That product is great as you can move the top and bottom parts independently of each other. The purpose is to have your bottom jaw forward and locked in place which will help keep your tongue from relaxing and slipping back into your throat when unconscious.</li><li>Switching sleep positions can do wonders as well. Side sleeping is the ideal position for reducing sleep apnea, it can even completely cure it in mild cases. If you occasionally or often sleep on your back it's worth looking into ways to train your body to sleep on your side instead. Many have found <a href="https://amzn.to/3gsBDqH" target="_blank">this</a>  product to work well. You put a couple of tennis balls into the back pocket of the t-shirt which stops you from unconsciously rolling onto your back when asleep. You should choose one size down from your usual shirt size as more of a snug fit will help keep the tennis balls in position. It's recommended to combine side sleeping with the mouthguard option above for at least two weeks to see if your symptoms improve.</li><li>If the mouth guard and side sleeping hasn't helped, you should look into trialing a CPAP device yourself. You should be able to find a local company that will let you rent a CPAP device and will remotely configure it to ensure it's working properly for you. We recommend trialing a CPAP device for at least two weeks.</li></ul>If you do end up being treated for sleep apnea with a CPAP machine, don't expect results overnight. Some people do have dramatic CPAP results from the first day but many others find that the recovery is cumulative. They may not feel an improvement until they have used a machine regularly for several weeks or months.`,
        },
        {
          name: "Chronic Nasal Problems",
          overview: `Nasal congestion is a common cause of brain fog. Symptoms of this include pressure behind the eyes and cheeks, a runny nose, frequent cough and postnasal drip. A variety of nasal conditions can be to blame including: <br><ul>  <li>Chronic Sinusitis (occurs when the sinuses are inflamed for a long period of time)</li>  <li>Allergic Rhinitis (allergic reaction to airborne allergens)</li>  <li>Deviated Septum (when the septum is significantly off center)</li>  <li>Enlarged Turbinates (inflammation of the mucosal membranes that cover the turbinates)</li>  <li>Nasal Polyps (painless, noncancerous growths on the lining of your nasal passages)</li></ul>People with asthma, cystic fibrosis, or immune system problems develop chronic sinusitis more often than others.`,
          treatment: `Many people have found relief of their nasal issues by regularly flushing their sinuses with neti pots, or squeeze bottles. A saltwater sinus flush is a safe and simple remedy for nasal congestion and sinus irritation that just about anyone can do at home. When rinsed through your nasal passages, saline can wash away allergens, mucus, and other debris, and help to moisten the mucous membranes. <a href="https://amzn.to/37zS9DM" target="_blank">This</a> NeilMed kit is cheap and works well. You could also look at <a href="https://amzn.to/2XrzoMV" target="_blank">stinging nettle extract</a> which is great for reducing the symptoms of nasal problems. If all else fails, you'll need to book an appointment with an ENT (Ear, Nose & Throat) specialist who will examine your nose with a nasal endoscope. If they find a problem the treatment may include nasal sprays, medication or surgery.`,
        },
        {
          name: "Hypothyroidism",
          overview: `Thyroid disorders are common and underdiagnosed - at least 10% of people will suffer from them at some point in their life, and women are much more affected than men. Hypothyroidism is when your body doesn't produce enough thyroid ho rmones, and this often results in feeling sluggish and weak amongst other symptoms such as brain fog. The severity of the symptoms will vary depending on the severity of the condition. The condition can be caused by a problem with the thyroid gland itself, problems with other glands that work with the thyroid, or a deficiency of iodine in the body. Other conditions can also cause an underactive thyroid, such as obstructive sleep apnea or UARS. Sometimes your thyroid will be considered underactive, but not at a level deemed important enough to require treatment, this is called 'subclinical' hypothyroidism. In many of the 'subclinical' cases the patient still experiences symptoms related to hypothyroidism but is unable to get treatment. If this is the case for you I strongly recommend looking at our treatment plan below as your brain fog and other symptoms may still be caused by an issue with your thyroid.`,
          treatment: `If you haven't had your thyroid function measured by a blood test this is the first thing you should be doing. See our blood test recommendations at the bottom of the page for more information on exactly what needs to be measured. The primary figure that we're interested in is TSH, which stands for thyroid stimulating hormone. Your pituitary gland produces this in an attempt to stimulate your thyroid to produce more thyroid hormones, so a higher TSH level indicates that your thyroid may not be functioning properly.<br>Many doctors will only treat patients with TSH levels above 10, with anything below that being treated as 'subclinical' hypothyroidism. If your TSH level comes back between 1.5 and 10, and your doctor will not treat you, you should seriously consider some thyroid supplements such as <a href="https://amzn.to/2XrFsoK" target="_blank">this</a> which works wonders for many patients experiencing subclinical hypothyroidism. If you are being treated for hypothryroidism with synthetic thyroid but are still experiencing thyroid related symptoms, you should look into a natural thyroid supplement such as Armour Thyroid which you will need a prescription for.`,
        },
        {
          name: "Vitamin D Deficiency",
          overview: `Vitamin D deficiency is very common, almost half of all Americans are reportedly lacking in it. It's very difficult to obtain your recommended daily intake through food alone. Your body makes it from cholesterol when skin is exposed to sunlight.`,
          treatment: `It's typically very easy to treat, if a blood test determines you're lacking in Vitamin D then a single pill daily is all you need. <a href="https://amzn.to/3fpvESf" target="_blank">This</a> is a good cheap source from Amazon.`,
        },
        {
          name: "Postural Orthostatic Tachycardia Syndrome (POTS)",
          overview: `Postural orthostatic tachycardia syndrome (POTS) is a condition that causes your heart rate to rapidly increase when you stand or sit up. People with POTS cannot coordinate the balancing act of blood vessel squeeze and heart rate response, which means the blood pressure cannot be kept steady and stable. The primary symptoms are feeling dizzy or faint, having blurry vision, nausea, vomiting, excess sweating and brain fog.`,
          treatment: `Your doctor will need to diagnose this so if you suspect that you have POTS you should book an appointment with your medical practitioner. A tilt table test is the preferred method to check for this condition, the patient is strapped into a moving table where their heart rate will be monitored as the table orientation changes. While there is no cure for POTS, various things can help with the symptoms such as medication, compression stockings, a high salt diet and exercise.`,
        },
        {
          name: "Hormonal Imbalances",
          overview: `Both men and women can have brain fog from hormone imbalances. For women, having too much or too little estrogen can cause brain fog among many other symptoms. For men, brain fog is a common side effect of low testosterone.`,
          treatment: `Hormonal imbalances are picked up in standard blood tests - see our blood test section at the bottom of the page for our full recommendation.  Typically doctors prescribe hormone replacement therapy (HRT) for low estrogen or testosterone. High estrogen treatment is dependent on the cause, often medication is prescribed but sometimes simply changing your diet can help to lower your estrogen levels.`,
        },
        {
          name: "Elevated Cortisol",
          overview: `Cortisol is a hormone produced by the adrenal glands. It gets released by the body to manifest our fight or flight response. Excessively high cortisol levels have been proven to kill brain cells, cause premature aging in the brain and decrease the rate at which new brain cells are created.Low cortisol is linked to 'adrenal fatigue', a condition which some are skeptical of, but regardless of the exact cause it's likely that having low cortisol is a contributing factor of HPA dysregulation.`,
          treatment: `The amount of cortisol in your body can be measured via blood, urine or saliva tests. Make an appointment with your doctor to organise getting tested. <br>High Cortisol can lead to <a href="https://www.healthline.com/health/cushings-syndrome" target="_blank">Cushing's syndrome</a>, and if left unmanaged can have serious consequences on your health. While chronically high levels of cortisol should absolutely be diagnosed and treated by a doctor, mildly elevated levels can often be successfully treated with Phosphatidylserine - a great supplement that lowers cortisol and can also supports brain health. It can be purchased from Amazon <a href="https://amzn.to/3i3RgoT" target="_blank">here</a>. <br>Low cortisol can be caused by Addison's disease, which is where the production of cortisol by your adrenal glands is too low. It can also indicate an issue with your pituitary gland if it is not sending proper signals to the adrenal glands. Adrenal supplements such as <a href="https://amzn.to/3i7b3DQ" target="_blank">this</a> can help to support proper adrenal function.`,
        },
        {
          name: "Anxiety",
          overview: `Brain fog is a common symptom of anxiety and stress. Trying to deal with the effects of brain fog can in itself be a source of stress. That stress generates anxiety and the constant worrying and racing thoughts can exhaust the mind and cause brain fog. Feedback loops like this can be difficult to treat. If you have constant brain fog and anxiety you may be suffering from generalized anxiety disorder.`,
          treatment: `Here's some tips for dealing with anxiety:<ul><li>Rest is critically important to help your body recover from mental stress. Lack of sleep in itself can cause anxiety so aim for at least eight hours of sleep per night, more if you need it.</li><li>Consider meditating daily. Consistent meditation programs the neural pathways in our brain, and improves our ability to regulate emotions. It really can dramatically help to lower mental anxiety and stress, which in turn can diminish brain fog.</li><li>For less serious cases of anxiety, you can look into supplements that improve cognitive function. Certain nootropics like <a href="https://amzn.to/3k6VjTt" target="_blank">ashwagandha, </a><a href="https://amzn.to/2Db1PaU" target="_blank">rhodiola</a> and <a href="https://amzn.to/2PkJydW" target="_blank">L-Theanine</a> are very effective at helping your body to cope with stress.</li><li>Stop taking stimulants. Caffeine  will only exacerbate the anxiety, so avoid anything with caffeine  in it like coffee or energy drinks. Avoid alcohol if possible, at least short term until your anxiety is under control.</li><li>Eat healthy and exercise regularly.</ul>If your anxiety is severe you should seek professional help. People suffer for months or even years with crippling anxiety, but anxiety disorders are treatable, there's no shame in asking for help! There are generally three avenues of treatment when it comes to anxiety disorders — medication (usually antidepressants), therapy or both. Psychotherapy involves working with a psychologist, social worker or therapist to learn about ways to better understand anxiety and manage your symptoms.`,
        },
        {
          name: "Candida",
          overview: `Candida is a very common cause of brain fog. It is a fungus that normally lives in low amounts in the body, but for a variety of reasons the normal balance of good bacteria and fungi can be disrupted, resulting in a candida 'overgrowth'. Thankfully the treatment is fairly straightforward, though it can take a while until the overgrowth is eradicated from your body.`,
          treatment: `<ul>  <li>Consume kefir daily. Milk kefir is a probiotic powerhouse and fantastic for overwhelming the candida infection. It's cheap, easy to make and delicious in smoothies! You can buy the powered version, or make it yourself (and have an endless supply) if you buy some starter kefir grains locally or from Amazon <a href="https://www.amazon.com/Kefir-Grains-Living-Probiotic-Enriched/dp/B004RCSWGG/ref=sr_1_1?dchild=1&keywords=milk+kefir&qid=1595553847&sr=8-1" target="_blank">here</a>. It's strongly recommended to go with the kefir grains over the powder, and try to drink at least a cup of milk kefir twice daily.</li>  <li>Avoid refined sugars, carbohydrates and alcohol wherever possible. These will only fuel the growth of the candida, so you really need to focus on eating as healthy as possible. Try to stick to a diet of vegetables, low sugar fruit, nuts and meat.</li>  <li>Supplement with products such as <a href="https://amzn.to/2HqoW3v" target="_blank">this</a> which will actively fight the candida overgrowth.</li>  <li>Try to reduce as much mental and physical stress as possible. Don't push your body too hard physically, and maintain a good sleep schedule. Your body is already in a state of high stress, don't add to it if you can help it!</li></ul>It is very common to experience die-off symptoms when doing a candida cleanse. As the yeast and bacteria overgrowth die off during treatment, they release toxins. This is known as the Herxheimer Reaction, where symptoms get worse before they get better.`,
        },
      ],
    };
  },

  methods: {
    scrollToConditions() {
      var elmnt = document.getElementById("results-text-container");
      elmnt.scrollIntoView({ behavior: "smooth" });
    },

    refreshPage() {
      this.$router.go();
    },
    saveButton() {
      this.conditions = [];
      this.numberOfConditions = [];
      this.symptoms.forEach((element) => {
        if (element.checked) {
          this.userSymptoms = this.userSymptoms + element.symptom_id + ",";
          this.checkedCount++;
        }
      });
      this.userSymptoms = this.userSymptoms.substring(0, this.userSymptoms.length - 1);

      if (this.checkedCount >= 5) {
        this.errorMessage = "";
        this.animationMessageSubmitting = true;
        setTimeout(() => (this.animationMessageSubmitting = false), 1000);
        setTimeout(() => (this.animationMessageProcessing = true), 1000);
        setTimeout(() => (this.animationMessageProcessing = false), 3000);
        setTimeout(() => (this.animationMessageReturning = true), 3000);
        setTimeout(() => (this.animationMessageReturning = false), 4000);
        setTimeout(() => (this.loadingAnimation = false), 4000);
        this.loadingAnimation = true;

        axios
          .post("https://fixmyfog.com/submit-symptoms", {
            userSymptoms: this.userSymptoms,
            uniqueID: this.$cookies.get("uniqueID"),
          })
          .then((response) => {
            console.log(response);
            var conditionData = response.data;
            if (conditionData.length == 3) {
              this.errorMessage = "Sorry, no results found. Please try adding more symptoms.";
            }
            this.submittedConditions = true;
            conditionData.forEach((item) => {
              console.log(item);
              if (item.matchLevel == "SOME SYMPOTOMS MATCH") {
                //Fixing on frontend so don't have to update backend
                item.matchLevel = "SOME SYMPTOMS MATCH";
              }
              if (item.matchLevel == "MANY SYMPOTOMS MATCH") {
                //Fixing on frontend so don't have to update backend
                item.matchLevel = "MANY SYMPTOMS MATCH";
              }
              if (item.fewSymptoms || item.someSymptoms || item.manySymptoms) {
                this.numberOfConditions.push(item);
              } else {
                this.conditions.push(item);
              }
            });

            if (this.numberOfConditions[0].fewSymptoms == "zero" && this.numberOfConditions[1].someSymptoms == "zero" && this.numberOfConditions[2].manySymptoms == "zero") {
              this.numberOfConditions = [];
            }

            setTimeout(this.scrollToConditions, 4500);
          });
      } else {
        if (this.checkedCount < 5) {
          this.errorMessage = "Please select at least five symptoms";
        }
      }
      this.checkedCount = 0;
    },

    emailButton() {
      axios
        .post("https://fixmyfog.com/send-email", {
          userSymptoms: this.userSymptoms,
          uniqueID: this.$cookies.get("uniqueID"),
          emailAddress: this.emailAddress,
        })
        .then((response) => {
          if (response.data == "OK") {
            this.submittedEmail = true;
          }
        });
    },
  },

  created() {
    this.causeList.sort(function (a, b) {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });

    // http://localhost:3001/get-symptoms
    axios.get("https://fixmyfog.com/get-symptoms").then((response) => {
      console.log(response);
      this.symptoms = response.data;
    });

    if (this.$cookies.get("uniqueID")) {
      this.$cookies.remove("uniqueID");
      this.$cookies.set("uniqueID", uuidv4());
    } else {
      this.$cookies.set("uniqueID", uuidv4());
    }
  },
};
</script>

<style>
#treatment-parent {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  width: 100%;
}

#button-container {
  padding: 20px 5px 0 5px;
  max-width: 1200px;
  font-size: 1.1rem;
  color: #343a40;
}

#checkbox-list-container {
  display: flex;
  margin: 10px 0 0 0;
  max-width: 1250px;
  height: auto;
  flex-wrap: wrap;
  padding: 10px 0 10px 0;
  border-radius: 10px;
}

.checkbox-list-container-item {
  box-sizing: border-box;
  height: auto;
  width: 310px;
  flex-grow: 1;
  padding: 5px;
  font-size: 1.1rem;
  text-align: left;
  color: #343a40;
}

#checkbox-button-container {
  margin-top: 10px;
}

#email-address-box {
  text-align: center;
  width: 350px;
  height: 25px;
  font-size: 1.1rem;
  margin: 12px 0 8px 0;
  outline: none;
}

#email-button {
  background-color: #3f5060;
  border: none;
  color: white;
  padding: 5px 16px;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  cursor: pointer;
  -webkit-transition-duration: 0.4s; /* Safari */
  transition-duration: 0.4s;
  font-size: 0.8rem;
  font-weight: 900;
  letter-spacing: 0.15em;
  margin-bottom: 20px;
  outline: none;
  height: 35px;
  width: 150px;
}

.button {
  background-color: #3f5060;
  border: none;
  color: white;
  padding: 15px 32px;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  margin: 4px 2px;
  cursor: pointer;
  -webkit-transition-duration: 0.4s; /* Safari */
  transition-duration: 0.4s;
  font-size: 0.9rem;
  font-weight: 900;
  letter-spacing: 0.15em;
  margin-bottom: 25px;
  outline: none;
}

#error-container {
  color: #a52a2a;
  text-align: center;
  font-size: 0.9em;
  font-weight: 900;
}

#results-text-container {
  display: flex;
  width: 100%;
  justify-content: center;
}

.results-text {
  padding-top: 5px;
  max-width: 1200px;
  font-size: 1.1rem;
  /* margin-bottom: 30px; */
  color: #343a40;
}

#checkbox-results-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
  text-align: left;
  color: #343a40;
}

#checkbox-results-container a {
  color: #343a40;
  text-decoration: underline;
}

.checkbox-results-item-container {
  display: flex;
  box-sizing: border-box;
  flex-wrap: wrap;
  min-height: 70px;
  max-width: 1200px;
  flex-wrap: wrap;
  margin-bottom: 15px;
  padding: 7px;
  border: 1px solid lightgrey;
}

.checkbox-results-item-header {
  display: flex;
  flex-grow: 1;
}

.checkbox-results-item-probability {
  border: 1px solid lightgrey;
  height: 20px;
  width: 185px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-align: center;
  padding-top: 6px;
  background-color: #2e4053;
  margin: 0 10px 5px 0;
}

.checkbox-results-item-title {
  box-sizing: border-box;
  height: 30px;
  flex-grow: 1;
  letter-spacing: 0.04em;
  font-weight: 700;
  color: #2d4052;
  font-size: 1.3rem;
  margin-left: 1px;
  padding-top: 2px;
}

.checkbox-results-treatment-title {
  margin: 12px 0px 3px 0px;
  border: 1px solid lightgrey;
  height: 20px;
  width: 185px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-align: center;
  padding-top: 6px;
  background-color: #2e4053;
  color: white;
}

.checkbox-results-treatment-spacer {
  height: 20px;
  flex-grow: 1;
  margin: 12px 0px 3px 0px;
  padding-top: 6px;
}

.animation-message {
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #343a40;
}

.loader {
  height: 60px;
  width: 60px;
  transform: scale(2, 2);
  transform-origin: center;
  margin-top: 200px;
  margin-bottom: 40px;
  margin-left: auto;
  margin-right: auto;
}

.ball-grid-pulse div {
  background-color: #3f5060;
}

.bold {
  font-weight: bold;
}

p {
  margin: 4px 0 0px 0;
}

.no-select {
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome, Edge, Opera and Firefox */
}

::-webkit-input-placeholder {
  text-align: center;
}

:-moz-placeholder {
  /* Firefox 18- */
  text-align: center;
}

::-moz-placeholder {
  /* Firefox 19+ */
  text-align: center;
}

:-ms-input-placeholder {
  text-align: center;
}

@media only screen and (max-width: 630px) {
  /* #intro-text-container {
    padding: 0px;
    width: 95%;
    font-size: 0.9rem;
    margin-bottom: 0px;
    margin-top: 5px;
  } */

  #results-text {
    width: 95%;
    font-size: 1rem;
  }

  #checkbox-list-container {
    margin: 0px;
    width: 97%;
  }

  .checkbox-results-item-header {
    flex-wrap: wrap;
  }

  .checkbox-results-item-probability {
    width: 95%;
  }

  .checkbox-results-item-title {
    height: 100%;
    /* margin-bottom: 5px; */
  }

  .checkbox-results-item-container {
    width: 97%;
  }

  .checkbox-results-treatment-title {
    width: 100%;
  }

  .checkbox-results-treatment-spacer {
    display: none;
  }

  #results-text {
    margin-bottom: 10px;
  }

  .checkbox-list-container-item {
    font-size: 0.9rem;
  }
}
</style>
